import { combineReducers } from '@reduxjs/toolkit';

import { resetOnLogoutReducer } from '@utils';

import {
  CLEAR_PEOPLE,
  DESELECT_PERSON,
  ModalMode,
  SELECT_MULTIPLE_PERSONS,
  SELECT_PERSON,
  SET_SELECTED_ACTIVITY,
  activityValuesTabs,
} from '../../common';
import { detailModes } from '../constants';
import {
  CLOSE_LONG_CRUISE_MODAL,
  OPEN_CREATE_LONG_CRUISE,
  OPEN_EDIT_LONG_CRUISE,
  OPEN_RECORD_DAYS,
  RECORD_LONG_CRUISE_ERROR,
  RECORD_LONG_CRUISE_REQUEST,
  RECORD_LONG_CRUISE_RESPONSE,
  SET_ACTIVE_TAB,
  SET_DETAIL_MODE,
  SET_SELECTED_LONG_CRUISE,
} from './actions';

const visible = (state = false, { type }) => {
  switch (type) {
    case OPEN_RECORD_DAYS:
    case OPEN_EDIT_LONG_CRUISE:
    case OPEN_CREATE_LONG_CRUISE:
      return true;
    case CLOSE_LONG_CRUISE_MODAL:
      return false;
    default: {
      return state;
    }
  }
};

const modalMode = (state = ModalMode.RECORD, { type }) => {
  switch (type) {
    case OPEN_CREATE_LONG_CRUISE: {
      return ModalMode.CREATE;
    }
    case OPEN_RECORD_DAYS: {
      return ModalMode.RECORD;
    }
    case OPEN_EDIT_LONG_CRUISE: {
      return ModalMode.EDIT;
    }
    default: {
      return state;
    }
  }
};

const loading = (state = false, { type }) => {
  switch (type) {
    case RECORD_LONG_CRUISE_REQUEST: {
      return true;
    }
    case RECORD_LONG_CRUISE_RESPONSE:
    case RECORD_LONG_CRUISE_ERROR: {
      return false;
    }
    default: {
      return state;
    }
  }
};

const userIds = (state = [], { type, payload }) => {
  switch (type) {
    case OPEN_RECORD_DAYS: {
      return payload.userIds || [];
    }
    case CLEAR_PEOPLE:
    case OPEN_CREATE_LONG_CRUISE: {
      return [];
    }
    case DESELECT_PERSON: {
      return state.filter(userId => userId != payload);
    }
    case SELECT_PERSON:
      return [...new Set([...state, payload])];
    case SELECT_MULTIPLE_PERSONS:
      return [...new Set([...state, ...payload])];
    default: {
      return state;
    }
  }
};

const dates = (state = {}, { type, payload }) => {
  switch (type) {
    case OPEN_RECORD_DAYS:
    case OPEN_EDIT_LONG_CRUISE: {
      return {};
    }
    case OPEN_CREATE_LONG_CRUISE: {
      return payload.dates;
    }
    default: {
      return state;
    }
  }
};

const selectedLongCruise = (state = null, { type, payload }) => {
  switch (type) {
    case OPEN_RECORD_DAYS:
    case OPEN_CREATE_LONG_CRUISE: {
      return null;
    }
    case SET_SELECTED_LONG_CRUISE: {
      return payload;
    }
    case SET_DETAIL_MODE: {
      if (payload === detailModes.SUGGEST) {
        return null;
      }
      return state;
    }
    default: {
      return state;
    }
  }
};

const activeTab = (state = activityValuesTabs.BASIC, { type, payload }) => {
  switch (type) {
    case OPEN_RECORD_DAYS:
    case OPEN_CREATE_LONG_CRUISE: {
      return activityValuesTabs.BASIC;
    }
    case SET_ACTIVE_TAB: {
      return payload;
    }
    case SET_SELECTED_ACTIVITY: {
      const { areSameAdvancedValues = true } = payload || {};
      return areSameAdvancedValues
        ? activityValuesTabs.BASIC
        : activityValuesTabs.ADVANCED;
    }
    default: {
      return state;
    }
  }
};

const allowRemovePersons = (state = false, { type, payload }) => {
  switch (type) {
    case OPEN_RECORD_DAYS:
    case OPEN_CREATE_LONG_CRUISE:
      return payload.allowRemovePersons;
    case OPEN_EDIT_LONG_CRUISE:
      return true;
    default:
      return state;
  }
};

const disableGetActivities = (state = false, { type, payload }) => {
  switch (type) {
    case OPEN_RECORD_DAYS:
      return payload.disableGetActivities;
    case OPEN_EDIT_LONG_CRUISE:
    case OPEN_CREATE_LONG_CRUISE:
      return false;
    default:
      return state;
  }
};

const disableFutureDays = (state = false, { type, payload }) => {
  switch (type) {
    case OPEN_RECORD_DAYS:
      return payload.disableFutureDays;
    case OPEN_EDIT_LONG_CRUISE:
    case OPEN_CREATE_LONG_CRUISE:
      return false;
    default:
      return state;
  }
};

const detailMode = (state = detailModes.SUGGEST, { type, payload }) => {
  switch (type) {
    case OPEN_RECORD_DAYS:
    case OPEN_CREATE_LONG_CRUISE: {
      const { dates: { start } = {} } = payload;
      if (start) {
        return detailModes.CREATE;
      }
      return detailModes.SUGGEST;
    }
    case SET_DETAIL_MODE: {
      return payload;
    }
    case OPEN_EDIT_LONG_CRUISE: {
      return detailModes.EDIT;
    }
    default: {
      return state;
    }
  }
};

const allowEditPersons = (state = false, { type, payload }) => {
  switch (type) {
    case OPEN_EDIT_LONG_CRUISE:
    case OPEN_CREATE_LONG_CRUISE: {
      const { allowEditOrAddPersonsPersons = false } = payload || {};
      return allowEditOrAddPersonsPersons;
    }
    case OPEN_RECORD_DAYS: {
      return false;
    }
    default: {
      return state;
    }
  }
};

const longCruiseReducer = combineReducers({
  visible,
  modalMode,
  loading,
  userIds,
  dates,
  selectedLongCruise,
  activeTab,
  detailMode,
  allowEditPersons,
  disableFutureDays,
  disableGetActivities,
  allowRemovePersons,
});

export default resetOnLogoutReducer(longCruiseReducer);
